import React, {useEffect, useState} from 'react';
import {Text, View} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import useGetOrderDetailsDetailsApi from '@/Hooks/SubscriptionDetails/useGetOrderDetails';
import useGetCsocSubscriptionDetailsApi from '@/Hooks/SubscriptionDetails/useCsocSubscription';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import {openInNewTab} from '@/Utils/CommonUtils';
import {useNavigation} from '@react-navigation/native';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import Icon from 'react-native-vector-icons/MaterialIcons';
import styles from './styles';
import {ScreenName} from '@/navigators/StackConstants';
import Spacer from '@/Atoms/Spacer';

// ---------- pure helpers ----------

const formatSimpleDate = (dateStr: string) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${months[parseInt(month, 10) - 1]} ${parseInt(day, 10)}, ${year}`;
};

const getSubscriptionBadgeStyle = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'active':
      return {backgroundColor: '#F0EBE1', textColor: '#5C4A2A'};
    case 'upcoming':
      return {backgroundColor: '#EEF3E2', textColor: '#3D5A1F'};
    case 'expired':
      return {backgroundColor: '#F0F0F0', textColor: '#666666'};
    case 'paused':
      return {backgroundColor: '#FFF3CD', textColor: '#856404'};
    default:
      return {backgroundColor: '#F0F0F0', textColor: '#333333'};
  }
};
const BOLD_KEYWORDS = ['Tournaments', 'Mentor'];

const splitBold = (message: string): [string, string] => {
  const numMatch = message.match(/^(\d+)(\s.+)$/);
  if (numMatch) return [numMatch[1], numMatch[2]];
  for (const keyword of BOLD_KEYWORDS) {
    if (message === keyword) return [message, ''];
    if (message.startsWith(keyword + ' '))
      return [keyword, message.slice(keyword.length)];
  }
  const wordMatch = message.match(/^(.*?)(\s(?:every|after|in|per|a\s).+)$/);
  if (wordMatch?.[1]) return [wordMatch[1], wordMatch[2]];
  return ['', message];
};

interface Props {
  userId: string;
  openMore: boolean;
  setOpenMore: (v: boolean) => void;
  setActiveProgressReports: (reports: any[]) => void;
  setShowProgressReportModal: (v: boolean) => void;
  handleResumeSubscription: (orderId: string) => void;
  setPauseOrderId: (id: string) => void;
  setPauseEndDate: (date: Date | null) => void;
  setPauseError: (err: string) => void;
  setPauseLimits: (limits: any) => void;
  setPauseLimitsError: (err: string) => void;
  setShowPauseModal: (v: boolean) => void;
  getPauseLimits: (params: {order_id: string}) => void;
  isMobileOrTab: boolean;
}

const SubscriptionCard = ({
  userId,
  openMore,
  setOpenMore,
  setActiveProgressReports,
  setShowProgressReportModal,
  handleResumeSubscription,
  setPauseOrderId,
  setPauseEndDate,
  setPauseError,
  setPauseLimits,
  setPauseLimitsError,
  setShowPauseModal,
  getPauseLimits,
  isMobileOrTab,
}: Props) => {
  const navigation = useNavigation();
  // Own isolated state — not affected by parent's API calls
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [invoice, setInvoice] = useState<any>(null);
  const [firstOrder, setFirstOrder] = useState('');
  const [planSummary, setPlanSummary] = useState<any>(null);

  const {mutate: getSubscriptionOrderDetails} = useGetOrderDetailsDetailsApi(
    (data: any) => {
      setOrderDetails(data?.order);
      setSubscriptions(data?.subscriptions ?? []);
      setInvoice(data?.invoice);
      setPlanSummary(data?.plan_summary);
    },
    () => {},
  );

  const {mutate: getCsocSubscriptionDetails} = useGetCsocSubscriptionDetailsApi(
    (data: any) => {
      const orderId = data?.data?.[0]?.order_id;
      if (orderId) {
        setFirstOrder(orderId);
      }
    },
    () => {},
  );
  const firstSub = subscriptions?.[0];
  const headerHasProgressReport = firstSub?.ptm?.progress_reports?.length > 0;
  const headerHasCertificate = !!firstSub?.certificate_url;
  const headerHasInvoice = !!(invoice as any)?.invoice_url;
  const firstSubStatus = firstSub?.status?.toLowerCase();
  const firstSubBatchType = (
    firstSub?.batch?.batch_type ||
    firstSub?.batch_type ||
    ''
  ).toLowerCase();
  const canResume = firstSubStatus === 'paused';
  const canPause =
    firstSubStatus === 'active' && firstSubBatchType.includes('personal');
  const condForMoreOptions =
    headerHasCertificate ||
    headerHasInvoice ||
    headerHasProgressReport ||
    canPause ||
    canResume;
  // Step 1: fetch subscription list to get the first order ID
  useEffect(() => {
    if (userId) {
      getCsocSubscriptionDetails(userId as any);
    }
  }, [userId]);

  // Step 2: once we have the first order ID, fetch order details
  useEffect(() => {
    if (firstOrder && userId) {
      getSubscriptionOrderDetails({orderId: firstOrder, userId});
    }
  }, [firstOrder, userId]);

  if (!orderDetails) return null;

  return (
    <>
      <Box
        onPress={() => setOpenMore(false)}
        flexDirection="column"
        style={styles.rtv}>
        <Box
          flexDirection="row"
          justifyContent="space-between"
          alignItems={isMobileOrTab ? 'flex-start' : 'center'}
          style={styles.btm}>
          <Box gap={isMobileOrTab ? 10 : 4} flexDirection="column">
            <Box gap={8} alignItems="center" type="row-flex-start">
              <CCText fontSize={FontSizes[18]} fontWeight={500}>
                {planSummary?.plan_name ?? orderDetails?.plan_title}
              </CCText>
              <Box
                style={[
                  {
                    backgroundColor: getSubscriptionBadgeStyle(
                      orderDetails?.status,
                    ).backgroundColor,
                  },
                  styles.pad,
                ]}>
                <CCText
                  fontSize={FontSizes[9]}
                  fontWeight={600}
                  color={
                    getSubscriptionBadgeStyle(orderDetails?.status).textColor
                  }>
                  {orderDetails?.status}
                </CCText>
              </Box>
            </Box>
            <Box gap={16} flexDirection={isMobileOrTab ? 'column' : 'row'}>
              <Box type="row-flex-start" style={styles.mnr}>
                <MaterialCommunityIcons
                  name="desktop-mac"
                  size={16}
                  color={CCColors.TextColor.Dark}
                />
                <CCText
                  fontWeight={400}
                  fontSize={FontSizes[14]}
                  color={CCColors.TextColor.Dark}>
                  {planSummary?.batch_type ?? subscriptions?.[0]?.batch_type}
                </CCText>
              </Box>
              <Box type="row-flex-start" style={styles.mnr}>
                <Icon
                  name="access-time"
                  size={16}
                  color={CCColors.TextColor.Dark}
                />
                <CCText
                  fontWeight={400}
                  fontSize={FontSizes[14]}
                  color={CCColors.TextColor.Dark}>
                  {formatSimpleDate(
                    subscriptions?.[0]?.subscription_start_date,
                  )}
                  {' \u2013 '}
                  {formatSimpleDate(subscriptions?.[0]?.subscription_end_date)}
                </CCText>
              </Box>
              <Box type="row-flex-start" style={styles.mnr}>
                <MaterialCommunityIcons
                  name="pound"
                  size={16}
                  color={CCColors.TextColor.Dark}
                />
                <CCText
                  fontWeight={400}
                  fontSize={FontSizes[14]}
                  color={CCColors.TextColor.Dark}>
                  {orderDetails?.order_id}
                </CCText>
              </Box>
            </Box>
          </Box>
          {condForMoreOptions && (
            <Box onPress={() => setOpenMore(true)} style={styles.bg}>
              <Icon name="more-vert" size={16} color={CCColors.Black} />
            </Box>
          )}
        </Box>

        {(planSummary?.features?.length > 0 ||
          subscriptions?.[0]?.features?.length > 0) && (
          <Box
            flexDirection={isMobileOrTab ? 'column' : 'row'}
            style={styles.rap}>
            {(planSummary?.features?.length > 0
              ? planSummary?.features
              : subscriptions?.[0]?.features ?? []
            ).map((feature: any) => {
              const iconMap: Record<string, string> = {
                classes: 'monitor',
                assignments: 'clipboard-text-outline',
                ptm: 'calendar-month-outline',
                tournaments: 'trophy-outline',
                psychology_class: 'brain',
              };
              const iconName = iconMap[feature?.type] || 'star-outline';
              const isTournament = feature?.type === 'tournaments';
              return (
                <Box
                  key={feature?.type}
                  flexDirection={isMobileOrTab ? 'column' : 'row'}
                  justifyContent="space-between"
                  alignItems={isMobileOrTab ? 'flex-start' : 'center'}
                  style={[
                    styles.basis,
                    {flexBasis: isMobileOrTab ? undefined : '31.5%'},
                  ]}>
                  <Box type="row-flex-start" style={{gap: 10, flex: 1}}>
                    <Box type="center" style={styles.thirty}>
                      <Box type="center" style={styles.iconbg}>
                        <MaterialCommunityIcons
                          name={iconName as any}
                          size={16}
                          color={CCColors.Black}
                        />
                      </Box>
                    </Box>
                    {(() => {
                      const [bold, rest] = splitBold(feature?.message ?? '');
                      return (
                        <Text style={styles.txt}>
                          {bold ? (
                            <Text style={styles.bold}>{bold}</Text>
                          ) : null}
                          {rest}
                        </Text>
                      );
                    })()}
                  </Box>
                  {isTournament && (
                    <Box
                      onPress={() => {
                        navigation.navigate(ScreenName.Tournaments, {
                          from: 'tournament',
                        });
                      }}
                      style={[styles.new, isMobileOrTab ? {marginTop: 8} : {}]}>
                      <CCText
                        fontSize={FontSizes[11]}
                        fontWeight="semibold"
                        color={CCColors.TextColor.Dark}>
                        View Tournament
                      </CCText>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {openMore &&
          (() => {
            const firstSub = subscriptions?.[0];
            const headerHasProgressReport =
              firstSub?.ptm?.progress_reports?.length > 0;
            const headerHasCertificate = !!firstSub?.certificate_url;
            const headerHasInvoice = !!(invoice as any)?.invoice_url;
            const firstSubStatus = firstSub?.status?.toLowerCase();
            const firstSubBatchType = (
              firstSub?.batch?.batch_type ||
              firstSub?.batch_type ||
              ''
            ).toLowerCase();
            const canResume = firstSubStatus === 'paused';
            const canPause =
              firstSubStatus === 'active' &&
              firstSubBatchType.includes('personal');

            const dropdownItem = (
              borderBottom: boolean,
              icon: string,
              label: string,
              onPress: () => void,
            ) => (
              <Box
                onPress={onPress}
                type="row-flex-start"
                style={[styles.icn, {borderBottomWidth: borderBottom ? 1 : 0}]}>
                <MaterialCommunityIcons
                  name={icon as any}
                  size={14}
                  color={CCColors.TextColor.Dark}
                />
                <CCText
                  fontSize={FontSizes[12]}
                  color={CCColors.TextColor.Dark}>
                  {label}
                </CCText>
              </Box>
            );

            return (
              <View style={styles.abs}>
                {headerHasProgressReport &&
                  dropdownItem(
                    headerHasCertificate || headerHasInvoice || true,
                    'chart-bar',
                    'Download Progress Report',
                    () => {
                      setActiveProgressReports(
                        firstSub?.ptm?.progress_reports ?? [],
                      );
                      setShowProgressReportModal(true);
                      setOpenMore(false);
                    },
                  )}
                {headerHasCertificate &&
                  dropdownItem(
                    headerHasInvoice || true,
                    'certificate-outline',
                    'Download Certificate',
                    () => {
                      openInNewTab(firstSub?.certificate_url);
                      setOpenMore(false);
                    },
                  )}
                {headerHasInvoice &&
                  dropdownItem(
                    true,
                    'file-document-outline',
                    'Download Invoice',
                    () => {
                      openInNewTab((invoice as any)?.invoice_url);
                      setOpenMore(false);
                    },
                  )}
                {canResume &&
                  dropdownItem(
                    canPause,
                    'play-circle-outline',
                    'Resume a class',
                    () => {
                      handleResumeSubscription(firstOrder);
                      setOpenMore(false);
                    },
                  )}
                {canPause &&
                  dropdownItem(
                    false,
                    'pause-circle-outline',
                    'Pause a class',
                    () => {
                      setPauseOrderId(firstOrder);
                      setPauseEndDate(null);
                      setPauseError('');
                      setPauseLimits(null);
                      setPauseLimitsError('');
                      setShowPauseModal(true);
                      getPauseLimits({order_id: firstOrder});
                      setOpenMore(false);
                    },
                  )}
              </View>
            );
          })()}
      </Box>
      <Spacer spacing={48} />
    </>
  );
};

export default SubscriptionCard;
